import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormControl, InputGroup, Table } from 'react-bootstrap';
import { priceSplitter } from '../../../../../../../utils/helpers/priceSplitter';
import { AddAdditionalItemForm } from './AddAdditionalItemForm';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../../store/rootReducer';
import { deleteAdditionItem, setAdditionalItems } from '../../../../../../../store/influencer/offerInfluencerSlice';
import { TrashIcon } from '../../../../../../../shared/Icons';
import { AdditionalItemMobile } from './AdditionalItemMobile';
import { FiInfo } from 'react-icons/fi';
import { useMediaQuery } from '@mui/material';
import { BootstrapBreakpoints } from '../../../../../../../enum/BootstrapBreakpoints';
import { CountryListWithTooltip } from '../../../../../../../shared/components/CoutryListWithTooltip';
import TooltipOverlay from '../../../../../../../shared/components/tooltip/Tooltip';

export function InfluencerAdditionalItemsTable() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { initialOffer, offerModalStatus, isCharity } = useSelector((state: IAllStates) => state.offerInfluencer);
    const [visibleAddAdditional, setVisibleAddAdditional] = useState(false);
    const isMobile = useMediaQuery(`(max-width: ${BootstrapBreakpoints.SM}px)`);

    function changeHandler(event: any, type: string, id: number) {
        const value = event.target.value;
        const additionalItemsCopy = [...initialOffer.distribution];
        const selectedItem =
            type === 'additional'
                ? additionalItemsCopy.find(item => item.id === id)
                : additionalItemsCopy.find(item => item.type === type);
        const index = selectedItem ? additionalItemsCopy.indexOf(selectedItem) : 0;
        additionalItemsCopy[index] = {
            ...additionalItemsCopy[index],
            price: +value,
        };
        dispatch(setAdditionalItems(additionalItemsCopy));
    }

    const deleteAdditionalItem = (index: number) => {
        dispatch(deleteAdditionItem(index));
    };

    return (
        <div className="table-offer-items">
            <Table
                responsive
                className={`table-head-bg mt-3 ${
                    (initialOffer.distribution?.length ?? 0) > 0 && 'mb-0'
                } d-md-table d-none`}
            >
                <thead className="table-head-bg">
                    <tr>
                        <th>{t('brand.campaign.book.offer.modal.additional_items')}</th>
                        <th>{t('influencer.offer_management.modal.details')}</th>
                        {initialOffer.distribution.every(item => item.type === 'additional') ? (
                            <>
                                <th></th>
                                <th></th>
                            </>
                        ) : (
                            <>
                                <th>{t('brand.campaign.book.offer.modal.duration')}</th>
                                {initialOffer.distribution.every(item => item.type === 'exclusivity') ? (
                                    <th></th>
                                ) : (
                                    <th>{t('brand.campaign.book.offer.modal.location')}</th>
                                )}
                            </>
                        )}
                        <th
                            className="text-end"
                            style={{ width: `${offerModalStatus === 'negotiate' ? '200px' : 'auto'}` }}
                        >
                            {t('brand.campaign.book.offer.modal.price')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {!isMobile &&
                        initialOffer.distribution?.map((item, index) => {
                            return (
                                <tr key={index}>
                                    <td className="pl-3">
                                        <div className="d-flex align-items-center">
                                            {offerModalStatus === 'negotiate' ? (
                                                <span onClick={() => deleteAdditionalItem(index)}>
                                                    <TrashIcon width="15" height="16" classes="cursor-pointer mr-2" />
                                                </span>
                                            ) : null}
                                            {item.type === 'additional'
                                                ? item.typeDescription
                                                : t(`influencer.offer_management.modal.${item?.type}`)}
                                            {item.type !== 'additional' && (
                                                <TooltipOverlay
                                                    className="ms-1 mb-0"
                                                    content={
                                                        item.type === 'exclusivity'
                                                            ? t(`brand.campaign.book.offer.modal.legal_tooltip`)
                                                            : t(`brand.campaign.book.offer.modal.distribution_tooltip`)
                                                    }
                                                >
                                                    <FiInfo size={14} />
                                                </TooltipOverlay>
                                            )}
                                        </div>
                                    </td>
                                    <td>{item.type === 'additional' ? '' : item?.typeDescription}</td>
                                    <td>
                                        {item?.duration === '' || !item?.duration
                                            ? ''
                                            : t(`brand.campaign.briefing.step8.${item?.duration}`)}
                                    </td>
                                    <td>
                                        <CountryListWithTooltip countries={item?.location} t={t} />
                                    </td>
                                    <td className="d-flex align-items-center justify-content-end">
                                        {offerModalStatus === 'negotiate' && !isCharity ? (
                                            <InputGroup size="sm" id="price-input">
                                                <InputGroup.Text>€</InputGroup.Text>
                                                <FormControl
                                                    min={0}
                                                    onChange={(e: any) =>
                                                        changeHandler && changeHandler(e, item.type, item.id ?? 0)
                                                    }
                                                    type="number"
                                                    defaultValue={Math.round(item?.price ?? 0)}
                                                    name="price"
                                                    placeholder="0"
                                                    id="price-input"
                                                    onKeyPress={(event: any) =>
                                                        (event?.key === '-' || event?.key === '+') &&
                                                        event.preventDefault()
                                                    }
                                                />
                                            </InputGroup>
                                        ) : (
                                            <p className={`${item?.price > 0 ? 'euro-symbol' : ''}`}>{`${priceSplitter(
                                                Math.round(item?.price)
                                            )}`}</p>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                </tbody>
            </Table>
            <h6 className="my-3 d-md-none">{t('brand.campaign.book.offer.modal.additional_items')}</h6>
            {isMobile &&
                initialOffer.distribution?.map((item, index) => {
                    return <AdditionalItemMobile changeHandler={changeHandler} additionalItem={item} index={index} />;
                })}
            {offerModalStatus === 'negotiate' && !visibleAddAdditional && !isCharity && (
                <Button
                    className="mb-2 mt-md-0 mt-2"
                    onClick={() => setVisibleAddAdditional(true)}
                    size="sm"
                    variant="outline-primary"
                >
                    {t('general.button.add_item')}
                </Button>
            )}
            {visibleAddAdditional && offerModalStatus === 'negotiate' && (
                <AddAdditionalItemForm setVisibility={setVisibleAddAdditional} />
            )}
        </div>
    );
}
